import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FileIcon, getFileCategory } from "@/components/ui/file-icon"
import { Download, ExternalLink, Loader2, ZoomIn, ZoomOut, RotateCw } from "lucide-react"

export interface PreviewableFile {
  displayName: string
  originalName?: string
  fileSize?: number
  downloadUrl: string
  contentType?: string
  fileType?: string
}

interface FilePreviewDialogProps {
  file: PreviewableFile | null
  open: boolean
  onClose: () => void
}

export function FilePreviewDialog({ file, open, onClose }: FilePreviewDialogProps) {
  const [textContent, setTextContent] = useState<string | null>(null)
  const [textLoading, setTextLoading] = useState(false)
  const [textError, setTextError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  const fileName = file ? (file.displayName || file.originalName || "文件预览") : ""
  const category = file ? getFileCategory(fileName, file.fileType, file.contentType) : "other"

  // 格式化 URL
  const getFullDownloadUrl = (url?: string) => {
    if (!url) return ""
    const token = localStorage.getItem("accessToken")
    let finalUrl = url
    if (!finalUrl.startsWith("http")) {
      if (finalUrl.startsWith("/download/")) {
        finalUrl = `/api${finalUrl}`
      }
      finalUrl = `${window.location.origin}${finalUrl}`
    }
    if (token) {
      finalUrl += finalUrl.includes("?") ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`
    }
    return finalUrl
  }

  const fullUrl = file ? getFullDownloadUrl(file.downloadUrl) : ""

  // 如果是文本或代码，尝试加载文本内容
  useEffect(() => {
    if (!open || !file || category !== "code") {
      setTextContent(null)
      setTextError(null)
      return
    }

    let isMounted = true
    setTextLoading(true)
    setTextError(null)

    fetch(fullUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.text()
      })
      .then((text) => {
        if (isMounted) {
          setTextContent(text.slice(0, 100000)) // 限制前 100KB 防止大文件卡顿
          setTextLoading(false)
        }
      })
      .catch((err) => {
        if (isMounted) {
          setTextError(`无法在线加载预览内容: ${err.message}`)
          setTextLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [open, file, category, fullUrl])

  // 重置缩放
  useEffect(() => {
    if (open) {
      setZoom(1)
      setRotation(0)
    }
  }, [open, file])

  const formatBytes = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
  }

  if (!file) return null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden bg-white rounded-2xl">
        {/* 顶部标题栏 */}
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3 min-w-0 pr-4">
            <FileIcon fileName={fileName} fileType={file.fileType} contentType={file.contentType} size="md" />
            <div className="min-w-0">
              <DialogTitle className="text-sm font-bold text-gray-900 truncate">
                {fileName}
              </DialogTitle>
              {file.fileSize ? (
                <p className="text-xs text-gray-400 mt-0.5">{formatBytes(file.fileSize)}</p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => {
                const a = document.createElement("a")
                a.href = fullUrl
                a.download = fileName
                a.click()
              }}
            >
              <Download className="w-3.5 h-3.5" />
              下载
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => window.open(fullUrl, "_blank")}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              新标签页
            </Button>
          </div>
        </DialogHeader>

        {/* 预览展示区域 */}
        <div className="flex-1 overflow-auto bg-gray-50/70 p-6 flex flex-col items-center justify-center min-h-[400px]">
          {category === "image" && (
            <div className="flex flex-col items-center w-full">
              {/* 图片控制工具栏 */}
              <div className="flex items-center gap-2 mb-3 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-sm border border-gray-100">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                  title="缩小"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs font-mono text-gray-600 px-1">{Math.round(zoom * 100)}%</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                  title="放大"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </Button>
                <div className="w-px h-4 bg-gray-200 mx-1" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  title="旋转"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                </Button>
              </div>

              <div className="overflow-auto max-h-[60vh] max-w-full flex items-center justify-center p-2">
                <img
                  src={fullUrl}
                  alt={fileName}
                  style={{
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transition: "transform 0.2s ease-out",
                  }}
                  className="max-h-[55vh] max-w-full object-contain rounded-lg shadow-md"
                />
              </div>
            </div>
          )}

          {category === "pdf" && (
            <iframe
              src={`${fullUrl}#toolbar=0`}
              title={fileName}
              className="w-full h-[65vh] border-0 rounded-xl shadow-sm bg-white"
            />
          )}

          {category === "video" && (
            <div className="w-full flex justify-center">
              <video
                controls
                autoPlay
                className="max-h-[65vh] max-w-full rounded-xl shadow-md bg-black"
                src={fullUrl}
              >
                您的浏览器不支持在线播放该视频。
              </video>
            </div>
          )}

          {category === "audio" && (
            <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center">
              <FileIcon fileName={fileName} fileType={file.fileType} contentType={file.contentType} size="lg" className="mx-auto mb-4" />
              <p className="text-sm font-medium text-gray-800 mb-4">{fileName}</p>
              <audio controls className="w-full" src={fullUrl}>
                您的浏览器不支持在线音频播放。
              </audio>
            </div>
          )}

          {category === "code" && (
            <div className="w-full h-[60vh] flex flex-col bg-gray-900 rounded-xl overflow-hidden shadow-md">
              <div className="bg-gray-800/80 px-4 py-2 text-xs font-mono text-gray-400 border-b border-gray-700 flex justify-between items-center">
                <span>文本/代码预览</span>
                <span>UTF-8</span>
              </div>
              <div className="flex-1 overflow-auto p-4 font-mono text-xs text-gray-200 leading-relaxed whitespace-pre">
                {textLoading && (
                  <div className="flex items-center justify-center h-full gap-2 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>正在载入文件内容...</span>
                  </div>
                )}
                {textError && <div className="text-red-400">{textError}</div>}
                {!textLoading && !textError && (textContent || "文件内容为空")}
              </div>
            </div>
          )}

          {category === "other" || category === "document" || category === "sheet" || category === "archive" ? (
            <div className="text-center p-8 max-w-sm bg-white rounded-2xl border border-gray-100 shadow-sm">
              <FileIcon fileName={fileName} fileType={file.fileType} contentType={file.contentType} size="lg" className="mx-auto mb-3" />
              <h4 className="text-sm font-bold text-gray-900 mb-1">{fileName}</h4>
              <p className="text-xs text-gray-500 mb-5">
                此格式暂不支持在浏览器中直接渲染，请下载至本地后查看。
              </p>
              <Button
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium rounded-full h-9 text-xs"
                onClick={() => {
                  const a = document.createElement("a")
                  a.href = fullUrl
                  a.download = fileName
                  a.click()
                }}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                立即下载文件
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
